import { useState } from 'react';
import type { CustomerAction } from '@ghostwright/api-client';
import { renderMarkdownParagraphs } from '../../lib/inline-markdown';

interface DraftCardProps {
  draftTitle: string;
  draftBody: string;
  draftMeta?: string;
  onAction: (action: CustomerAction) => void;
  disabled?: boolean;
}

export default function DraftCard({ draftTitle, draftBody, draftMeta, onAction, disabled }: DraftCardProps) {
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const handleReject = () => {
    if (!rejectMode) {
      setRejectMode(true);
      return;
    }
    if (rejectReason.trim().length > 0) {
      onAction({ kind: 'reject-draft', reason: rejectReason.trim() });
      setRejectMode(false);
      setRejectReason('');
    }
  };

  return (
    <div
      className="mt-6 bg-card border border-border rounded-card overflow-hidden shadow-[0_1px_2px_hsl(var(--foreground)/0.04),0_8px_24px_-8px_hsl(var(--foreground)/0.08)]"
      role="region"
      aria-label="Drafted proposal"
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-background">
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground inline-flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-primary/70" aria-hidden="true" />
          Drafted from your turn
        </span>
        {draftMeta && (
          <span className="text-xs text-placeholder">{draftMeta}</span>
        )}
      </div>

      <div className="px-8 py-7 font-display italic text-[19px] leading-relaxed text-foreground [font-variation-settings:'opsz'_48,'SOFT'_80]">
        {draftTitle && (
          <p className="not-italic font-medium text-heading mb-3.5">{draftTitle}</p>
        )}
        {renderMarkdownParagraphs(draftBody, 'mt-3.5')}
      </div>

      <div className="px-5 py-4 border-t border-border bg-background flex flex-wrap items-center gap-3 max-sm:flex-col max-sm:items-stretch">
        {rejectMode ? (
          <div className="flex-1 flex items-center gap-3 w-full">
            <input
              type="text"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleReject(); }}
              placeholder="What's wrong with it?"
              autoFocus
              className="flex-1 text-sm py-2.5 px-4 border border-border rounded-button bg-background text-foreground placeholder:text-placeholder focus:outline-none focus:border-primary focus:shadow-[0_0_0_3px_hsl(var(--primary)/0.15)]"
            />
            <button
              type="button"
              disabled={disabled || rejectReason.trim().length === 0}
              onClick={handleReject}
              className="text-sm font-medium py-2.5 px-4 bg-primary text-primary-foreground rounded-button shadow-[inset_0_1px_0_hsl(var(--primary-foreground)/0.15)] transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              Send
            </button>
            <button
              type="button"
              onClick={() => { setRejectMode(false); setRejectReason(''); }}
              className="text-sm text-muted-foreground py-2.5 px-2 hover:text-foreground"
            >
              Cancel
            </button>
          </div>
        ) : (
          <>
            <button
              type="button"
              disabled={disabled}
              onClick={() => onAction({ kind: 'accept-draft' })}
              className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground text-sm font-medium py-2.5 px-[18px] rounded-button min-h-[44px] shadow-[inset_0_1px_0_hsl(var(--primary-foreground)/0.15)] transition-[opacity,transform] duration-80 ease-out hover:opacity-90 hover:-translate-y-px disabled:opacity-50 max-sm:justify-center"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 7l3 3 5-6"/></svg>
              Lock as the spine
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={() => onAction({ kind: 'edit-draft', text: '' })}
              className="inline-flex items-center gap-1.5 text-sm font-medium py-2.5 px-[18px] min-h-[44px] border border-border rounded-button bg-background text-foreground transition-[border-color,background] duration-80 ease-out hover:border-foreground/30 hover:bg-card disabled:opacity-50 max-sm:justify-center"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2 11l9-9 1 1-9 9-2 1z"/></svg>
              Edit
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={() => onAction({ kind: 'request-variants' })}
              className="inline-flex items-center gap-1.5 text-sm font-medium py-2.5 px-[18px] min-h-[44px] border border-border rounded-button bg-background text-foreground transition-[border-color,background] duration-80 ease-out hover:border-foreground/30 hover:bg-card disabled:opacity-50 max-sm:justify-center"
            >
              Give me 2 more like this
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={handleReject}
              className="text-sm font-medium py-2.5 px-2 min-h-[44px] text-muted-foreground transition-colors duration-80 hover:text-foreground disabled:opacity-50 max-sm:text-center"
            >
              That's wrong, say why
            </button>
          </>
        )}
      </div>
    </div>
  );
}
